/**
 * Shared pub/sub module for Document Readiness invalidation.
 *
 * A2F-758: The Gantt (usePlanningData) and the Document Signoff tiles need to
 * refresh immediately after a document sign-off or owner change — without a
 * hard reload. This is the signal channel that bridges the mutation site
 * (DocumentReadinessPage handlers) to the Gantt subscriber.
 *
 * Why a separate channel (not piggyback on `invalidateConsistency`)?
 * A document sign-off is *not* a Review consistency change. Piggybacking on
 * the Consistency channel would conflate two unrelated domain events, make the
 * Consistency subscribers refetch unnecessarily on every sign-off, and make it
 * harder to reason about each channel's scope. The ~30-line cost of a clean
 * channel is worth the conceptual clarity. (The Consistency piggyback remains
 * the documented emergency fallback only if this channel hits an unforeseen
 * blocker — which it did not.)
 *
 * Contract:
 *   - Emitters call `invalidateDocumentReadiness(projectId)` inside the
 *     `.then()` of a successful mutation (sign, setOwners). Firing in `.then()`
 *     — not at the optimistic update — ensures the server write has landed
 *     before usePlanningData triggers its server-side refetch.
 *   - Subscribers pass a listener to `onDocumentReadinessInvalidated` and
 *     receive the projectId whenever an emitter fires. They decide whether to
 *     refetch (typically: only if the fired projectId matches their active
 *     project).
 *   - Subscribers MUST unsubscribe in their cleanup (use the returned function)
 *     to avoid leaks when pages unmount.
 *
 * Intentionally NOT batched / debounced — mirrors the Consistency channel's
 * deliberate stance. If a burst of sign-offs triggers N emits we accept N
 * refetches; debouncing prematurely risks masking legitimate back-to-back
 * sign-off completions. Add a coalescing layer only if it becomes a measured
 * perf issue.
 */

type Listener = (projectId: string) => void;

const listeners = new Set<Listener>();

/** Register a listener. Returns an unsubscribe function. */
export function onDocumentReadinessInvalidated(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Fire all listeners. Safe to call from any sign-off completion path. */
export function invalidateDocumentReadiness(projectId: string): void {
  // Snapshot before iterating — a listener that unsubscribes itself during
  // notification must not mutate the set we're iterating over.
  const snapshot = Array.from(listeners);
  for (const fn of snapshot) {
    try {
      fn(projectId);
    } catch (err) {
      // A misbehaving listener must not break other subscribers or the
      // emitter's sign-off completion flow.
      // eslint-disable-next-line no-console
      console.error('documentReadinessInvalidation listener threw:', err);
    }
  }
}
